import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Command, useCommandState } from 'cmdk';

import { VesselMessage } from '../../../../types/VesselMessage';
import { queries } from '../../api/queryKeys/queries';
import { formatVessel } from '../../helpers/formatVessel.helper';
import { useDebounce } from '../../hooks/useDebounce';
import { useAutoSelectFirstCommandItem } from '../hooks/useAutoSelectFirstCommandItem';
import { useShortcutMenu } from '../hooks/useShortcutMenu';

import { CommandEmptyState } from './CommandEmptyState';
import { CommandErrorState } from './CommandErrorState';
import { CommandItem } from './CommandItem';
import { CommandSkeletonList } from './CommandSkeletonList';

export const VesselSearch = () => {
    const search = useCommandState((state) => state.search);
    const { setOpen } = useShortcutMenu();
    const debouncedSearch = useDebounce(search, 300);

    const query = useQuery(
        queries.vessels.pagination({
            searchQuery: debouncedSearch,
            page: 0,
            active: true,
        })
    );

    useAutoSelectFirstCommandItem(query.data?.content[0]?.id);

    const navigate = useNavigate();

    const onVesselSelect = (vessel: VesselMessage) => {
        navigate({
            to: '/vessel/$id',
            params: { id: vessel.id },
        });
        setOpen(false);
    };

    if (query.isPending) {
        return <CommandSkeletonList />;
    }

    if (query.isError) {
        return <CommandErrorState />;
    }

    if (!query.data || query.data.content.length === 0) {
        return <CommandEmptyState />;
    }

    return (
        <Command.List>
            {query.data.content.map((vessel: VesselMessage) => (
                <CommandItem key={vessel.id} value={vessel.id} onSelect={() => onVesselSelect(vessel)}>
                    {formatVessel({ vessel })}
                </CommandItem>
            ))}
        </Command.List>
    );
};
